import React, { useState, useEffect } from "react";
import ComparisonsComponent from "../components/comparisons-component";
import ComparisonsTable403b from "../components/comparisons-table-403b";
import { ComparisonsAlert } from "../../../components/modals/comparisons-alert";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";

interface Comparison {
  id: number;
  comparisonName: string;
  rateOfReturn: number;
  expectedIncomePercentage: number;
  yearsUntilIncome: number;
}

const defaultItem: Comparison = {
  id: 0,
  comparisonName: "",
  rateOfReturn: 0,
  expectedIncomePercentage: 0,
  yearsUntilIncome: 0,
};

const emptyCollection = { values: [defaultItem] };

const parseJsonSafely = (json: string | undefined): Comparison[] => {
  if (!json) return emptyCollection.values;
  const values = JSON.parse(json).values;
  return values;
};

interface Comparisons403bPropsWithAutoSave {
  client: ClientType;
  formsDirty: any;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
}

const Comparisons403b: React.FC<Comparisons403bPropsWithAutoSave> = ({
  client,
  formsDirty,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  saveStatus,
  lastSaveTime,
}) => {
  const [comparisons, setComparisons] = useState<Comparison[]>(
    parseJsonSafely(client.comparisons403b) || []
  );
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    if (
      !client.calculated_403b_value ||
      !Array.isArray(client.calculated_403b_value) ||
      client.calculated_403b_value.length === 0
    )
      return;

    const generatedData = client.calculated_403b_value.map(
      (item: any, index: number) => {
        const row: any = {
          year: item.year,
          balance403b: item.balance,
          income403b: item.income,
        };

        comparisons.forEach((comparison, compIndex) => {
          const balanceKey = `balance${compIndex}`;
          const diffKey = `diff${compIndex}`;
          const incomeKey = `income${compIndex}`;
          const diffIncomeKey = `diffIncome${compIndex}`;

          const balance =
            item.balance * (1 + (index * comparison.rateOfReturn) / 100);
          const income = balance * (comparison.expectedIncomePercentage / 100);

          row[balanceKey] = balance || 0;
          row[diffKey] = balance - item.balance || 0;
          row[incomeKey] = income || 0;
          row[diffIncomeKey] = income - item.income || 0;
        });

        return row;
      }
    );

    setData(generatedData);
  }, [client.calculated_403b_value, comparisons]);

  const hasValidComparisons = comparisons.some(
    (comp) =>
      comp.comparisonName !== "" ||
      comp.rateOfReturn !== 0 ||
      comp.expectedIncomePercentage !== 0 ||
      comp.yearsUntilIncome !== 0
  );

  return (
    <div className="py-6">
      <ComparisonsComponent
        client={client}
        setComparisons={setComparisons}
        formsDirty={formsDirty}
        setFormsDirty={setFormsDirty}
        fetchClientData={fetchClientData}
        resetFormsDirtyState={resetFormsDirtyState}
        saveStatus={saveStatus}
        lastSaveTime={lastSaveTime}
      />
      {(!client.calculated_403b_value ||
        !Array.isArray(client.calculated_403b_value) ||
        client.calculated_403b_value.length === 0) && <ComparisonsAlert />}
      {client.calculated_403b_value &&
        Array.isArray(client.calculated_403b_value) &&
        client.calculated_403b_value.length !== 0 &&
        hasValidComparisons && (
          <ComparisonsTable403b data={data} comparisons={comparisons} />
        )}
    </div>
  );
};

export default Comparisons403b;
